import React, { useState } from 'react';
import { Upload, FileText, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

/**
 * Vehicle Document OCR Component
 * Allows uploading Fahrzeugschein (vehicle registration) and extracts data via OCR
 */
const VehicleDocumentOCR = ({ onDataExtracted }) => {
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [extracted, setExtracted] = useState(false);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Check file type
    if (!selectedFile.type.startsWith('image/')) {
      toast.error('Bitte ein Bild auswählen (JPG, PNG)');
      return;
    }

    // Check file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 10MB)');
      return;
    }

    setFile(selectedFile);
    setExtracted(false);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(selectedFile);
  };

  const handleUploadAndExtract = async () => {
    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(
        `${backendUrl}/vehicles/extract-document`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );

      const data = response.data;

      if (data.success) {
        setExtracted(true);
        toast.success('Fahrzeugdaten erfolgreich ausgelesen!');
        
        // Pass extracted data to parent
        if (onDataExtracted) {
          onDataExtracted(data.vehicle_data);
        }
      } else {
        toast.error(data.error || 'Fehler beim Auslesen der Daten');
      }
    } catch (error) {
      console.error('OCR Error:', error);
      toast.error('Fehler beim Verarbeiten des Dokuments');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setExtracted(false);
  };

  return (
    <Card className="p-4 bg-card border-border">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-foreground">Fahrzeugschein hochladen (optional)</h3>
        </div>

        <p className="text-sm text-muted-foreground">
          Laden Sie ein Foto Ihres Fahrzeugscheins hoch, um alle Daten automatisch auszulesen.
        </p>

        {/* Upload Area */}
        {!file ? (
          <label className="block cursor-pointer">
            <div className="border-2 border-dashed border-border rounded-lg p-8 hover:border-primary transition-colors">
              <div className="flex flex-col items-center gap-3 text-center">
                <Upload className="h-12 w-12 text-muted-foreground" />
                <div>
                  <p className="font-medium text-foreground">Dokument hochladen</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Klicken oder Datei hierher ziehen
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    JPG, PNG (max. 10MB)
                  </p>
                </div>
              </div>
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
        ) : (
          /* Preview & Actions */
          <div className="space-y-3">
            {/* Image Preview */}
            <div className="relative rounded-lg overflow-hidden border border-border">
              <img
                src={preview}
                alt="Fahrzeugschein Preview"
                className="w-full h-48 object-contain bg-muted"
              />
              {extracted && (
                <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-md flex items-center gap-1 text-xs font-medium">
                  <CheckCircle className="h-3 w-3" />
                  Erfolgreich
                </div>
              )}
            </div>

            {/* File Info */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground truncate flex-1">
                {file.name}
              </span>
              <span className="text-muted-foreground ml-2">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </span>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              {!extracted ? (
                <>
                  <Button
                    onClick={handleUploadAndExtract}
                    disabled={uploading}
                    className="flex-1"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Wird verarbeitet...
                      </>
                    ) : (
                      <>
                        <FileText className="mr-2 h-4 w-4" />
                        Daten auslesen
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleReset}
                    disabled={uploading}
                  >
                    Abbrechen
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={handleReset} className="flex-1">
                  Neues Dokument hochladen
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default VehicleDocumentOCR;
